import {
  Box,
  FormControl,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, JSX, useMemo } from "react";

import { CoverageOption, FormState } from "../types";

import CensusCheckbox from "./census-checkbox";
import CensusDropzone from "./census-dropzone";
import CensusError from "./census-error";
import CensusGroupClasses from "./census-group-classes";
import CensusRadio from "./census-radio";
import CensusTitle from "./census-title";
import { COVERAGES, LOADING, YES_NO_OPTIONS } from "./constants";
import { downloadTemplate } from "./utils";

const {
  DENTAL,
  VISION,
  STD,
  LTD,
  BASIC_LIFE,
  VOLUNTARY_LIFE,
  VOLUNTARY_AD,
  INDIVIDUAL_DISABILITY,
  STATE_DISABILITY,
  WHOLE_LIFE,
  OTHER_COV,
} = COVERAGES;

type Props = {
  formState: FormState;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
};

const CensusForm = ({
  formState,
  updateFormStateValue,
  onChange,
}: Props): JSX.Element => {
  const isLoading = useMemo(
    () => formState.state === LOADING,
    [formState.state],
  );

  const handleChangeOptions = (
    coverageName: string,
    nameField: string,
    value: string | { value?: string } | CoverageOption,
  ) => {
    const newSelectedCoverages = {
      ...formState.selectedCoverages,
      [coverageName]: {
        ...formState.selectedCoverages[coverageName],
        [nameField]: value,
      },
    };
    updateFormStateValue("selectedCoverages", newSelectedCoverages);
  };

  return (
    <>
      <CensusTitle title="Company Details" />
      <Box px={2} mb={3}>
        <CensusRadio
          name="fullTimeEmployees"
          label="Are all employees on this census considered full-time employees?"
          onChange={onChange}
          options={YES_NO_OPTIONS}
          value={formState.fullTimeEmployees}
          disabled={isLoading}
        />
      </Box>
      <CensusGroupClasses
        classNames={formState.censusGroupClasses}
        maxNumberOfClasses={10}
        updateFormStateValue={updateFormStateValue}
      />
      <CensusTitle title="Coverages" />
      <Box px={2} mb={3}>
        <FormControl>
          <FormLabel required>
            Which coverages are you quoting for this RFP?
          </FormLabel>
          <FormGroup>
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={DENTAL.name}
              label={DENTAL.label}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={VISION.name}
              label={VISION.label}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={STD.name}
              label={STD.label}
              groupClasses={formState.censusGroupClasses}
              updateFormStateValue={updateFormStateValue}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={LTD.name}
              label={LTD.label}
              groupClasses={formState.censusGroupClasses}
              updateFormStateValue={updateFormStateValue}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={BASIC_LIFE.name}
              label={BASIC_LIFE.label}
              groupClasses={formState.censusGroupClasses}
              updateFormStateValue={updateFormStateValue}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={VOLUNTARY_LIFE.name}
              label={VOLUNTARY_LIFE.label}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              onChangeOptions={handleChangeOptions}
              coverageName={VOLUNTARY_AD.name}
              label={VOLUNTARY_AD.label}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              coverageName={INDIVIDUAL_DISABILITY.name}
              label={INDIVIDUAL_DISABILITY.label}
              hideQuestions={true}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              coverageName={STATE_DISABILITY.name}
              label={STATE_DISABILITY.label}
              hideQuestions={true}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              coverageName={WHOLE_LIFE.name}
              label={WHOLE_LIFE.label}
              hideQuestions={true}
            />
            <CensusCheckbox
              disabled={isLoading}
              selectedCoverages={formState.selectedCoverages}
              onChange={onChange}
              coverageName={OTHER_COV.name}
              label={OTHER_COV.label}
              hideQuestions={true}
            />
          </FormGroup>
        </FormControl>
      </Box>
      <CensusTitle title="Census File" />
      <Box px={2} mb={3}>
        <FormControl>
          <FormLabel required>
            Which row number has your column headers?
          </FormLabel>
          <TextField
            name="row"
            sx={{ marginTop: 1 }}
            variant="outlined"
            label="Headers Row Number"
            type="number"
            required
            value={formState.row}
            onChange={onChange}
            disabled={isLoading}
          />
        </FormControl>
      </Box>
      <CensusTitle title="Upload Census" />
      <Typography variant="body">
        Don’t have the template?{" "}
        <Typography
          variant="bodyBold"
          color="brandPrimary"
          className="pointer"
          onClick={downloadTemplate}
        >
          Click here to download.
        </Typography>
      </Typography>
      {formState.censusError && formState.censusFile ? (
        <CensusError
          censusError={formState.censusError}
          file={formState.censusFile}
        />
      ) : (
        <CensusDropzone
          file={formState.censusFile}
          fileError={formState.fileError}
          onChange={(file) => updateFormStateValue("censusFile", file)}
          onFileError={(error) => updateFormStateValue("fileError", error)}
        />
      )}
    </>
  );
};

export default CensusForm;
